import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Button, Divider, Stack, Typography } from "@mui/material";
import { t } from "i18next";
import { Link as RouterLink } from "react-router-dom";
import { useTheme } from "../../../context/ThemeContext";
import { getStages } from "../../../shared/British";
import { Stage } from "../../../shared/CaseConfiguration";
import { Metric, MetricProps } from "../../general/DataAtoms";
import { signedTone } from "../../general/dataAtoms/shared";
import { EmptyStateFrame, EmptyStateHeader } from "../../general/EmptyState";

/**
 * The bargaining view of a fully settled case. A settled case is a completed
 * outcome, not missing data, so this states what happened rather than what is
 * absent — and it replaces the parameter controls entirely, since there is no
 * open range left to bargain over or model.
 *
 * Every figure is an agreed result read off the case's precomputed settlement
 * summary, never re-derived here. Each is omitted rather than shown as zero
 * when the case predates the field it comes from.
 */
export const SettledEmptyState = ({
  caseId,
  settledValue,
  midpointDeviation,
  exposureRemoved,
  stage,
}: {
  caseId: string;
  /** Total agreed settlement value to the clients (+ received, − paid). */
  settledValue?: number;
  /** Settled value minus the rational midpoint; negative = conceded. */
  midpointDeviation?: number;
  /** Worst-case improvement from settling; positive = adverse exposure removed. */
  exposureRemoved?: number;
  /** Stage at which the case became fully settled. */
  stage?: Stage;
}) => {
  const { theme } = useTheme();

  // higher is better for both movements shown here (a settlement above the
  // midpoint, and a worst case lifted by settling), so each takes its tone
  // from its sign
  const facts: MetricProps[] = [];

  if (settledValue !== undefined)
    facts.push({
      label: t("case.analytics.settlement.settled.settledAt.label"),
      value: settledValue,
      caption: t("case.analytics.settlement.settled.settledAt.caption"),
      // a level, not a movement — stays in ink
    });

  if (midpointDeviation !== undefined)
    facts.push({
      label: t("case.analytics.settlement.settled.vsMidpoint.label"),
      value: midpointDeviation,
      caption: t(
        `case.analytics.settlement.settled.vsMidpoint.${
          midpointDeviation < 0 ? "below" : midpointDeviation > 0 ? "above" : "at"
        }`
      ),
      tone: signedTone(midpointDeviation),
    });

  if (exposureRemoved !== undefined)
    facts.push({
      label: t("case.analytics.settlement.settled.exposure.label"),
      value: exposureRemoved,
      caption: t("case.analytics.settlement.settled.exposure.caption"),
      tone: signedTone(exposureRemoved),
    });

  // distance to trial is derived, so the copy cannot drift from the stage
  const stages = getStages();
  const stagesBeforeTrial =
    stage === undefined
      ? 0
      : Math.max(0, stages.indexOf(Stage.Trial) - stages.indexOf(stage));
  const stageName =
    stage === undefined ? "" : t("case.stages." + Stage[stage], Stage[stage]);
  const stageSentence =
    stage === undefined
      ? undefined
      : stagesBeforeTrial > 0
        ? t("case.analytics.settlement.settled.stageLine", {
            stage: stageName,
            count: stagesBeforeTrial,
          })
        : t("case.analytics.settlement.settled.stageLineAtTrial", {
            stage: stageName,
          });

  return (
    <EmptyStateFrame>
      {/* tinted positive: a settlement is a result, not something missing */}
      <EmptyStateHeader
        icon={<CheckIcon />}
        tint={theme.palette.data.positive}
        title={t("case.analytics.settlement.settled.title")}
        intro={t("case.analytics.settlement.settled.intro")}
      />

      {facts.length > 0 && (
        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem />}
          sx={{
            width: "100%",
            bgcolor: "background.paper",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {facts.map((fact) => (
            <Metric
              key={fact.label}
              {...fact}
              sx={{ flex: 1, px: theme.spacing(2), py: theme.spacing(1.5) }}
            />
          ))}
        </Stack>
      )}

      {stageSentence && (
        <Typography
          variant="caption"
          sx={{
            color: "text.disabled",
            textAlign: "center",
          }}
        >
          {stageSentence}
        </Typography>
      )}

      <Button
        variant="contained"
        component={RouterLink}
        to={`/cases/${caseId}/analytics/settlement`}
        endIcon={<ChevronRightIcon />}
      >
        {t("case.analytics.settlement.settled.viewTerms")}
      </Button>
    </EmptyStateFrame>
  );
};
